import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import config from './config';
import { CourseSummary, fetchCoursesCached } from './courseApi';
import { getOrganizationHeaders, withOrganizationSlug } from './orgContext';

interface Category {
  id: number;
  name: string;
  description: string;
  detailedDescription?: string;
  tips?: string;
  isEnabled?: boolean; // Whether the category is enabled/active
}

type Course = CourseSummary;

interface CourseWithCategories extends Course {
  categories: Category[];
}

const normalizeCourseRef = (value: string) => value.trim().toLowerCase();

const findCourseByRef = (items: CourseWithCategories[], value?: string): CourseWithCategories | null => {
  const rawRef = (value ?? '').trim();
  if (!rawRef) {
    return null;
  }

  const normalizedRef = normalizeCourseRef(rawRef);
  const byNav = items.find(
    (item) => (item.inforPagenav ?? '').trim().toLowerCase() === normalizedRef
  );
  if (byNav) {
    return byNav;
  }

  const numericRef = Number(rawRef);
  if (Number.isFinite(numericRef) && numericRef > 0) {
    return items.find((item) => item.id === numericRef) ?? null;
  }

  return null;
};

const getCourseRouteRef = (course: CourseWithCategories): string => {
  const nav = (course.inforPagenav ?? '').trim();
  return nav || String(course.id);
};

interface UserEnrollment {
  id: number;
  userId: number;
  courseId: number;
  courseName: string;
  enrollmentDate: string;
  completionPercentage: number;
  isCompleted: boolean;
  completionDate: string | null;
  subscriptionTier: number;
}

const Courses: React.FC = () => {
  const { isLoading: authLoading, isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { courseRef } = useParams<{ courseRef?: string }>();
  const [courses, setCourses] = useState<CourseWithCategories[]>([]);
  const [enrolledCourses, setEnrolledCourses] = useState<UserEnrollment[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<CourseWithCategories | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrollingCourseId, setEnrollingCourseId] = useState<number | null>(null);
  const [enrollmentError, setEnrollmentError] = useState<string | null>(null);
  const [showCoursesMenu, setShowCoursesMenu] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.innerWidth < 768;
  });

  const navigate = useNavigate();
  const API_BASE_URL = config.apiBaseUrl + '/api' || 'https://localhost:7298/api';

  // Fetch all courses
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        setError(null);
        const loadedCourses = await fetchCoursesCached() as CourseWithCategories[];
        setCourses(loadedCourses);
        if (loadedCourses.length > 0) {
          const shouldAutoSelectCourse = typeof window !== 'undefined' && window.innerWidth >= 768;
          const matchedCourse = findCourseByRef(loadedCourses, courseRef);
          setSelectedCourse(
            matchedCourse ?? (courseRef ? null : shouldAutoSelectCourse ? loadedCourses[0] : null)
          );
          if (matchedCourse && typeof window !== 'undefined' && window.innerWidth < 768) {
            setShowCoursesMenu(false);
          }
        }
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('Failed to load courses. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [API_BASE_URL, courseRef]);

  useEffect(() => {
    if (courses.length === 0) {
      return;
    }

    const matchedCourse = findCourseByRef(courses, courseRef);
    if (matchedCourse) {
      setSelectedCourse(matchedCourse);
      if (typeof window !== 'undefined' && window.innerWidth < 768) {
        setShowCoursesMenu(false);
      }
      setError(null);
      return;
    }

    if (courseRef) {
      setError('Course not found.');
    }
  }, [courses, courseRef]);

  // Fetch user enrollments when authenticated
  useEffect(() => {
    const fetchEnrollments = async () => {
      if (!isAuthenticated && !authLoading) return;

      try {
        const token = await getAccessTokenSilently();
        if (!token) return;

        const response = await axios.get(`${API_BASE_URL}/courses/user/enrollments`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...getOrganizationHeaders(),
          },
        });
        setEnrolledCourses(response.data);
      } catch (err) {
        console.error('Error fetching enrollments:', err);
      }
    };

    if (!authLoading) {
      fetchEnrollments();
    }
  }, [isAuthenticated, authLoading, getAccessTokenSilently]);

  const isEnrolled = (courseId: number) => {
    return enrolledCourses.some(enrollment => enrollment.courseId === courseId);
  };

  const hasFullAccess = (courseId: number) => {
    const enrollment = enrolledCourses.find(e => e.courseId === courseId);
    return enrollment && enrollment.subscriptionTier >= 999;
  };

  const selectedCourseEnrollment = useMemo(() => {
    if (!selectedCourse) {
      return null;
    }

    return enrolledCourses.find((enrollment) => enrollment.courseId === selectedCourse.id) ?? null;
  }, [selectedCourse, enrolledCourses]);

  const handleEnroll = async (courseId: number) => {
    if (!isAuthenticated) {
      setEnrollmentError(null);
      await loginWithRedirect({
        appState: {
          returnTo: `${window.location.pathname}${window.location.search}`,
        },
      });
      return;
    }

    try {
      setEnrollingCourseId(courseId);
      setEnrollmentError(null);

      const token = await getAccessTokenSilently();
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await axios.post(
        `${API_BASE_URL}/courses/${courseId}/enroll`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...getOrganizationHeaders(),
          },
        }
      );

      // Add the new enrollment to our list
      setEnrolledCourses([...enrolledCourses, response.data]);
      setEnrollmentError(null);
    } catch (err: any) {
      console.error('Error enrolling in course:', err);
      setEnrollmentError(
        err.response?.data?.message || 'Failed to enroll in course. Please try again.'
      );
    } finally {
      setEnrollingCourseId(null);
    }
  };

  const getDifficultyColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'beginner':
        return 'bg-green-100/70 text-green-900';
      case 'intermediate':
        return 'bg-blue-100/70 text-blue-900';
      case 'advanced':
        return 'bg-red-100/70 text-red-900';
      default:
        return 'bg-gray-100/70 text-gray-900';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const showEnrollmentsPanel = false;

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-gradient-to-br from-app-page-from to-app-page-to"
      style={{
      width: '100%',
      height: '100vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div className="app-breadcrumb-bar">
        <div className="app-breadcrumb-row">
          <button onClick={() => navigate('/')} className="app-breadcrumb-link">Home</button>
          <span className="app-breadcrumb-sep">›</span>
          <span className="app-breadcrumb-current">Courses</span>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-3 pb-40 md:p-4 md:pb-4 lg:p-6 lg:pb-6">
        {error && (
          <div className="mb-3 md:mb-4 bg-red-50 border border-red-200 rounded-lg p-3 md:p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {enrollmentError && (
          <div className="mb-3 md:mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 md:p-4">
            <p className="text-yellow-700 text-sm">{enrollmentError}</p>
          </div>
        )}

        <div className={`grid gap-4 md:gap-6 ${showEnrollmentsPanel && isAuthenticated && enrolledCourses.length > 0 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-12' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
          {/* Courses List */}
          <div className={`${showCoursesMenu ? 'block' : 'hidden'} md:block ${showEnrollmentsPanel && isAuthenticated && enrolledCourses.length > 0 ? 'md:col-span-2 lg:col-span-3' : 'md:col-span-1 lg:col-span-1'}`}>
            <div className="bg-white rounded-lg shadow-md overflow-hidden h-full">
              {!loading && courses.length > 0 && (
                <div className="px-3 py-2.5 border-b border-gray-100 bg-gray-50">
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center text-lg shrink-0">
                      📚
                    </div>
                    <div className="min-w-0 text-left">
                      <p className="text-sm font-bold text-gray-900 leading-tight">Courses</p>
                      <p className="text-xs font-semibold text-gray-500 mt-0.5">{courses.length} course{courses.length !== 1 ? 's' : ''} available</p>
                    </div>
                  </div>
                </div>
              )}
              {loading ? (
                <div className="p-4 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                </div>
              ) : courses.length === 0 ? (
                <div className="p-6 md:p-8 text-center">
                  <div className="text-4xl mb-3">📘</div>
                  <p className="text-gray-700 text-sm font-semibold">No courses available</p>
                  <p className="text-gray-500 text-xs mt-1">Please check back shortly.</p>
                </div>
              ) : (
                <div className="p-2 md:p-3 space-y-2 md:space-y-3 max-h-[300px] md:max-h-[400px] lg:max-h-[calc(100vh-250px)] overflow-y-auto">
                  {courses.map(course => (
                    <div
                      key={course.id}
                      onClick={() => {
                        setSelectedCourse(course);
                        setShowCoursesMenu(false);
                        navigate(withOrganizationSlug(`/courses/${getCourseRouteRef(course)}`));
                      }}
                      className={`p-3 md:p-4 rounded-xl border cursor-pointer transition-all ${
                        selectedCourse?.id === course.id
                          ? 'bg-indigo-200 border-indigo-700 ring-2 ring-indigo-300 shadow-lg'
                          : 'bg-indigo-50/40 border-indigo-300 shadow-md hover:border-indigo-500 hover:shadow-lg hover:bg-indigo-100/60 hover:-translate-y-0.5'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-8 h-8 md:w-9 md:h-9 rounded-lg flex items-center justify-center text-sm md:text-base shrink-0 ${
                            selectedCourse?.id === course.id ? 'bg-indigo-400' : 'bg-blue-200'
                          }`}>
                            🏠
                          </div>
                          <div className="min-w-0">
                            <h3 className={`font-semibold text-sm md:text-[15px] leading-snug truncate ${selectedCourse?.id === course.id ? 'text-indigo-900' : 'text-blue-900'}`}>{course.name}</h3>
                            <div className="mt-0.5 flex items-center gap-1">
                              <span className={`text-xs md:text-sm font-semibold ${selectedCourse?.id === course.id ? 'text-indigo-700' : 'text-blue-700'}`}>
                                {course.difficultyLevel}
                              </span>
                            </div>
                          </div>
                        </div>
                        {isEnrolled(course.id) && (
                          <span className="inline-block px-2 py-0.5 rounded-md text-[10px] md:text-xs font-semibold bg-green-100 text-green-800">
                            Enrolled
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Course Details */}
          <div className={`${showEnrollmentsPanel && isAuthenticated && enrolledCourses.length > 0 ? 'md:col-span-2 lg:col-span-5' : 'md:col-span-1 lg:col-span-2'}`}>
            {selectedCourse ? (
              <>
              <div className="bg-white rounded-lg shadow-md overflow-hidden h-full">
                {/* Course Header */}
                <div className="p-4 md:p-6 bg-gradient-to-r from-blue-500 to-blue-600">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCourse(null);
                          setShowCoursesMenu(true);
                        }}
                        className="md:hidden inline-flex items-center justify-center rounded-lg border border-white/30 bg-white/10 px-2.5 py-2 text-white hover:bg-white/15"
                        aria-label="Back"
                        title="Back"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l-5 4 5 4" />
                        </svg>
                      </button>
                      <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center text-lg shrink-0">📘</div>
                      <div className="min-w-0">
                        <h1 className="text-xl md:text-2xl font-bold text-white leading-snug truncate">{selectedCourse.name}</h1>
                        <p className="text-app-header-muted text-xs md:text-sm mt-0.5">Course overview and learning path</p>
                      </div>
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-2">
                      <span className="text-xs font-semibold bg-white/20 rounded-full px-2.5 py-1 whitespace-nowrap">
                        {selectedCourse.categories?.length ?? 0} topic{(selectedCourse.categories?.length ?? 0) !== 1 ? 's' : ''}
                      </span>
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(selectedCourse.difficultyLevel)}`}>
                        {selectedCourse.difficultyLevel}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 md:mt-3 flex items-center gap-2 md:gap-3 flex-wrap">
                    {selectedCourse.prerequisites && (
                      <span className="text-white text-xs md:text-sm">Prerequisites: {selectedCourse.prerequisites}</span>
                    )}
                  </div>
                  {((selectedCourseEnrollment?.completionPercentage ?? 0) > 0) && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-[11px] md:text-xs text-app-header-muted mb-1">
                        <span>Progress</span>
                        <span>{Math.round(selectedCourseEnrollment?.completionPercentage ?? 0)}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-white/20 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-white/70 transition-all duration-300"
                          style={{ width: `${Math.max(0, Math.min(100, selectedCourseEnrollment?.completionPercentage ?? 0))}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Course Content */}
                <div className="p-4 md:p-6 max-h-none md:max-h-[500px] lg:max-h-[calc(100vh-330px)] overflow-y-auto">
                  {/* Action Buttons at Top */}
                  <div className="hidden md:flex gap-3 mb-4 md:mb-6 justify-end">
                    {!isEnrolled(selectedCourse.id) ? (
                      <>
                        <button
                          onClick={() => {
                            window.location.href = withOrganizationSlug(`/course-details/${getCourseRouteRef(selectedCourse)}`);
                          }}
                          className="inline-flex items-center justify-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-indigo-500 text-white rounded-lg text-sm md:text-base font-semibold hover:bg-indigo-600 transition-colors"
                        >
                          <span className="text-sm leading-none">📘</span>
                          <span>Syllabus</span>
                        </button>
                        <button
                          onClick={() => {
                            window.location.href = withOrganizationSlug(`/practice?courseId=${getCourseRouteRef(selectedCourse)}`);
                          }}
                          className="inline-flex items-center justify-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-green-500 text-white rounded-lg text-sm md:text-base font-semibold hover:bg-green-600 transition-colors"
                        >
                          <span className="text-sm leading-none">📖</span>
                          <span>Questions</span>
                        </button>
                        <button
                          onClick={() => handleEnroll(selectedCourse.id)}
                          disabled={enrollingCourseId === selectedCourse.id}
                          className="px-4 md:px-6 py-2 md:py-3 bg-blue-500 text-white rounded-lg text-sm md:text-base font-semibold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {enrollingCourseId === selectedCourse.id ? (
                            <span className="flex items-center justify-center gap-2">
                              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                              Enrolling...
                            </span>
                          ) : !isAuthenticated ? (
                            'Sign in to Enroll'
                          ) : (
                            'Enroll Now'
                          )}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            window.location.href = withOrganizationSlug('/course-details/' + selectedCourse.id);
                          }}
                          className="inline-flex items-center justify-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-indigo-500 text-white rounded-lg text-sm md:text-base font-semibold hover:bg-indigo-600 transition-colors"
                        >
                          <span className="text-sm leading-none">📘</span>
                          <span>Syllabus</span>
                        </button>
                        <button
                          onClick={() => {
                            window.location.href = withOrganizationSlug('/practice?courseId=' + selectedCourse.id);
                          }}
                          className="inline-flex items-center justify-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-green-500 text-white rounded-lg text-sm md:text-base font-semibold hover:bg-green-600 transition-colors"
                        >
                          <span className="text-sm leading-none">�</span>
                          <span>Questions</span>
                        </button>
                        {!isAuthenticated && (
                          <button
                            onClick={() => {
                              window.location.href = withOrganizationSlug('/practice?courseId=' + selectedCourse.id);
                            }}
                            className="inline-flex items-center justify-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-green-500 text-white rounded-lg text-sm md:text-base font-semibold hover:bg-green-600 transition-colors"
                          >
                            <span className="text-sm leading-none">🚀</span>
                            <span>Start Learning</span>
                          </button>
                        )}
                        {!hasFullAccess(selectedCourse.id) && (
                          <button
                            onClick={() => {
                              window.location.href = withOrganizationSlug('/billing?courseId=' + selectedCourse.id);
                            }}
                            className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg text-sm md:text-base font-semibold hover:from-yellow-600 hover:to-orange-600 transition-colors shadow-md"
                          >
                            🔓 Upgrade to Full Access
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  
                  {/* Description */}
                  <div className="mb-4 md:mb-6 border-l-4 border-blue-400 pl-4">
                    <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-400 inline-block shrink-0" />
                      Overview
                    </h2>
                    <div 
                      className="html-content text-sm md:text-base text-gray-600 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: selectedCourse.description }}
                    />
                  </div>

                  {/* Categories */}
                  {selectedCourse.categories && selectedCourse.categories.length > 0 && (
                    <div className="mb-4 md:mb-6">
                      <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-indigo-400 inline-block shrink-0" />
                        Topics covered
                      </h2>
                      <div className="grid grid-cols-1 gap-2 md:gap-3">
                        {selectedCourse.categories.map(category => (
                          <div key={category.id} className="p-2 md:p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <h3 className="font-semibold text-gray-900 text-xs md:text-sm">{category.name}</h3>
                            {category.description && (
                              <p className="text-xs text-gray-600 mt-1">{category.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Enrollment Info */}
                  <div className="hidden md:block mb-4 md:mb-6 p-3 md:p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs md:text-sm text-gray-600">
                      {isEnrolled(selectedCourse.id)
                        ? '✓ You are enrolled in this course. Start learning by selecting topics!'
                        : 'Click the button below to enroll in this course and start learning.'}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="hidden md:flex gap-3">
                    {!isEnrolled(selectedCourse.id) ? (
                      <>
                        <button
                          onClick={() => {
                            window.location.href = withOrganizationSlug(`/course-details/${getCourseRouteRef(selectedCourse)}`);
                          }}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-indigo-500 text-white rounded-lg text-sm md:text-base font-semibold hover:bg-indigo-600 transition-colors"
                        >
                          <span className="text-sm leading-none">📘</span>
                          <span>Syllabus</span>
                        </button>
                        <button
                          onClick={() => {
                            window.location.href = withOrganizationSlug(`/practice?courseId=${getCourseRouteRef(selectedCourse)}`);
                          }}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-green-500 text-white rounded-lg text-sm md:text-base font-semibold hover:bg-green-600 transition-colors"
                        >
                          <span className="text-sm leading-none">📖</span>
                          <span>Questions</span>
                        </button>
                        <button
                          onClick={() => handleEnroll(selectedCourse.id)}
                          disabled={enrollingCourseId === selectedCourse.id}
                          className="flex-1 px-4 md:px-6 py-2 md:py-3 bg-blue-500 text-white rounded-lg text-sm md:text-base font-semibold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {enrollingCourseId === selectedCourse.id ? (
                            <span className="flex items-center justify-center gap-2">
                              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                              Enrolling...
                            </span>
                          ) : !isAuthenticated ? (
                            'Sign in to Enroll'
                          ) : (
                            'Enroll Now'
                          )}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            window.location.href = withOrganizationSlug('/course-details/' + selectedCourse.id);
                          }}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-indigo-500 text-white rounded-lg text-sm md:text-base font-semibold hover:bg-indigo-600 transition-colors"
                        >
                          <span className="text-sm leading-none">📘</span>
                          <span>Syllabus</span>
                        </button>
                        {!isAuthenticated && (
                          <button
                            onClick={() => {
                              window.location.href = withOrganizationSlug(`/practice?courseId=${getCourseRouteRef(selectedCourse)}`);
                            }}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-green-500 text-white rounded-lg text-sm md:text-base font-semibold hover:bg-green-600 transition-colors"
                          >
                            <span className="text-sm leading-none">🚀</span>
                            <span>Start Learning</span>
                          </button>
                        )}
                        {!hasFullAccess(selectedCourse.id) && (
                          <button
                            onClick={() => {
                              window.location.href = withOrganizationSlug('/billing?courseId=' + selectedCourse.id);
                            }}
                            className="flex-1 px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg text-sm md:text-base font-semibold hover:from-yellow-600 hover:to-orange-600 transition-colors shadow-md"
                          >
                            🔓 Upgrade to Full Access
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-2 py-4 min-h-[72px]">
                <div className="flex flex-nowrap items-center justify-end gap-2 overflow-x-auto">
                  {!isEnrolled(selectedCourse.id) ? (
                    <>
                      <button
                        onClick={() => {
                          setSelectedCourse(null);
                          setShowCoursesMenu(true);
                        }}
                        aria-label="Back"
                        title="Back"
                        className="shrink-0 inline-flex items-center justify-center px-3 py-2 bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-200 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l-5 4 5 4" />
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          window.location.href = withOrganizationSlug(`/course-details/${getCourseRouteRef(selectedCourse)}`);
                        }}
                        className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 px-3 py-2 bg-indigo-500 text-white rounded-lg text-sm font-semibold hover:bg-indigo-600 transition-colors"
                      >
                        <span className="text-sm leading-none">📘</span>
                        Syllabus
                      </button>
                      <button
                        onClick={() => {
                          window.location.href = withOrganizationSlug(`/practice?courseId=${getCourseRouteRef(selectedCourse)}`);
                        }}
                        className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-semibold hover:bg-green-600 transition-colors"
                      >
                        <span className="text-sm leading-none">📖</span>
                        Questions
                      </button>
                      <button
                        onClick={() => handleEnroll(selectedCourse.id)}
                        disabled={enrollingCourseId === selectedCourse.id}
                        className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {enrollingCourseId === selectedCourse.id ? (
                          <span className="flex items-center justify-center gap-2 whitespace-nowrap">
                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                            Enrolling...
                          </span>
                        ) : !isAuthenticated ? (
                          <>
                            <span className="text-sm leading-none">🔐</span>
                            Sign in to Enroll
                          </>
                        ) : (
                          <>
                            <span className="text-sm leading-none">✍️</span>
                            Enroll Now
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setSelectedCourse(null);
                          setShowCoursesMenu(true);
                        }}
                        aria-label="Back"
                        title="Back"
                        className="shrink-0 inline-flex items-center justify-center px-3 py-2 bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-200 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l-5 4 5 4" />
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          window.location.href = withOrganizationSlug(`/course-details/${getCourseRouteRef(selectedCourse)}`);
                        }}
                        className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 px-3 py-2 bg-indigo-500 text-white rounded-lg text-sm font-semibold hover:bg-indigo-600 transition-colors"
                      >
                        <span className="text-sm leading-none">📘</span>
                        Syllabus
                      </button>
                      <button
                        onClick={() => {
                          window.location.href = withOrganizationSlug(`/practice?courseId=${getCourseRouteRef(selectedCourse)}`);
                        }}
                        className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-semibold hover:bg-green-600 transition-colors"
                      >
                        <span className="text-sm leading-none">📖</span>
                        Questions
                      </button>
                      {!hasFullAccess(selectedCourse.id) && (
                        <button
                          onClick={() => {
                            window.location.href = withOrganizationSlug('/billing?courseId=' + selectedCourse.id);
                          }}
                          className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg text-sm font-semibold hover:from-yellow-600 hover:to-orange-600 transition-colors shadow-md"
                        >
                          <span className="text-sm leading-none">🔓</span>
                          Upgrade
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              </>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-8 md:p-12 text-center h-full flex items-center justify-center">
                <div>
                  <div className="text-5xl mb-4">📚</div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Pick a course to begin</h3>
                  <p className="text-gray-500 text-sm md:text-base">Select a course from the left to view overview, topics, and actions.</p>
                </div>
              </div>
            )}
          </div>

          {/* Your Enrollments - Third Column */}
          {showEnrollmentsPanel && isAuthenticated && enrolledCourses.length > 0 && (
            <div className="md:col-span-2 lg:col-span-4">
              <div className="bg-white rounded-lg shadow-md overflow-hidden h-full">
                <div className="p-3 md:p-4 bg-green-50 border-b">
                  <h2 className="text-base md:text-lg font-semibold text-gray-900">Your Enrollments</h2>
                  <p className="text-xs md:text-sm text-gray-600 mt-1">{enrolledCourses.length} course{enrolledCourses.length !== 1 ? 's' : ''} enrolled</p>
                </div>
                <div className="divide-y max-h-[300px] md:max-h-[400px] lg:max-h-[calc(100vh-250px)] overflow-y-auto">
                  {enrolledCourses.map(enrollment => (
                    <div 
                      key={enrollment.id} 
                      className="p-3 md:p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => {
                        // Navigate to course with practice questions
                        window.location.href = withOrganizationSlug('/practice?courseId=' + enrollment.courseId);
                      }}
                    >
                      <h3 className="font-semibold text-gray-900 text-xs md:text-sm">{enrollment.courseName}</h3>
                      <div className="mt-2 md:mt-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-600">Progress</span>
                          <span className="text-xs font-semibold text-gray-900">{Math.round(enrollment.completionPercentage)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${enrollment.completionPercentage}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="mt-2 md:mt-3 text-xs text-gray-600">
                        <p>Enrolled: {formatDate(enrollment.enrollmentDate)}</p>
                        {enrollment.isCompleted && enrollment.completionDate && (
                          <p className="text-green-600 font-semibold mt-1">
                            ✓ Completed: {formatDate(enrollment.completionDate)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {!selectedCourse && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-2 py-4 min-h-[72px] z-40">
          <div className="flex flex-nowrap items-center justify-end gap-2 overflow-x-auto">
            <button
              onClick={() => navigate('/')}
              className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 px-3 py-2 bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg text-sm font-semibold hover:bg-indigo-200 transition-colors"
            >
              <span className="text-sm leading-none">🏠</span>
              Home
            </button>
            <button
              onClick={() => navigate(withOrganizationSlug('/practice'))}
              className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-semibold hover:bg-green-600 transition-colors"
            >
              <span className="text-sm leading-none">📖</span>
              Questions
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Courses;
